/**
 * Lift2Fit — the CRM's front door (`#/crm/signup`).
 *
 * Public route: a coach or coaching business creates its own account here and
 * lands directly in the workspace. It submits `accountType: 'coach'`, so the
 * API grants the coach role and the account's own free plan (one client, no
 * card). This is a deliberate product choice, not a privilege switch — it can
 * never create an admin, and it never touches another tenant's data. A visitor
 * who actually wants the tracker is one link away at `#/signup`.
 */
import { useEffect, useState } from 'react';
import { API_BASE } from '@/lib/api';
import { useSession } from '@/lib/useSession';
import { Logo } from '@/components/Logo';
import { Button } from '@/components/ui';
import { ArrowRightIcon, CheckIcon, LockIcon, SparkIcon } from '@/lib/icons';
import { LiveMessage } from '../components';
import { Link, navigate } from '../router';
import { pendingInvitationCode } from '../crm/CrmJoinTeamScreen';
import { homeFor } from '../screens/LoginScreen';
import { CONSENT_STATEMENT, CONSENT_SUBLINE, POLICY_VERSION } from '../legal/policy';

// 0034: coaching is paid, so signup sells the workspace, not a free wedge.
const FREE_TIER_POINTS = [
  'Your roster, calendar, bookings, payments and insights',
  'A coaching workspace with AI assistance across every client',
  'Your own lifter profile, included from the Coach plan upward',
];

export function CrmSignupScreen() {
  const session = useSession();
  const [businessName, setBusinessName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  // 0024: unticked by default. A coaching business accepts the same three
  // documents as a lifter, and the API enforces it.
  const [acceptedPolicy, setAcceptedPolicy] = useState(false);
  const [consentError, setConsentError] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [tone, setTone] = useState<'info' | 'danger' | 'success'>('info');

  /**
   * `?next=` brings a newly-signed-up coach back to the invitation link they
   * arrived on (0033). Internal paths only — never an absolute URL.
   */
  const destination = (fallback: string): string => {
    // A pending team invitation outranks the usual landing spot.
    if (pendingInvitationCode() !== null) return '/crm/join-team';
    if (typeof window === 'undefined') return fallback;
    const raw = new URLSearchParams(window.location.hash.split('?')[1] ?? '').get('next');
    if (raw === null) return fallback;
    const value = decodeURIComponent(raw);
    if (!value.startsWith('/') || value.startsWith('//') || value.includes('\\')) return fallback;
    return value;
  };

  useEffect(() => {
    if (session.isAuthenticated) navigate(destination(homeFor(session.user)), { replace: true });
  }, [session.isAuthenticated, session.user]);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setMessage(null);
    if (businessName.trim().length < 2) {
      setTone('danger');
      setMessage('Give your coaching business a name.');
      return;
    }
    if (!email.includes('@')) {
      setTone('danger');
      setMessage('Enter the email address you will sign in with.');
      return;
    }
    if (password.length < 8) {
      setTone('danger');
      setMessage('Passwords are at least 8 characters.');
      return;
    }
    if (!acceptedPolicy) {
      setConsentError(true);
      setTone('danger');
      setMessage(`${CONSENT_STATEMENT} Tick the box to continue.`);
      return;
    }

    setBusy(true);
    setTone('info');
    setMessage('Creating your workspace…');
    const result = await session.register({
      email,
      password,
      displayName: businessName.trim(),
      accountType: 'coach',
      acceptPolicy: true,
      policyVersion: POLICY_VERSION,
    });
    setBusy(false);

    if (!result.ok) {
      setTone('danger');
      setMessage(result.error ?? 'That did not work. Check your details and try again.');
      return;
    }
    setTone('success');
    setMessage('Workspace ready.');
    // A business lands in the CRM, never on the lifter dashboard.
    navigate(destination('/crm'));
  };

  return (
    <div className="l2f-app flex min-h-screen flex-col" data-testid="crm-signup">
      <div className="mx-auto flex w-full max-w-[1120px] flex-1 flex-col items-center justify-center gap-10 px-4 py-10 md:flex-row md:items-stretch md:gap-16 md:px-8">
        {/* left: the promise */}
        <section className="flex w-full flex-col justify-center gap-6 md:max-w-[46%]">
          <Link to="/" aria-label="Lift2Fit home" className="flex items-center gap-3 text-ink">
            <Logo variant="horizontal" className="h-8 w-[150px]" />
          </Link>
          <span className="inline-flex w-fit items-center gap-2 border border-hairline px-3 py-1 text-[10px] font-bold uppercase tracking-[0.1em] text-ink">
            <SparkIcon className="text-sm text-accent" /> Lift2Fit CRM
          </span>
          <h1 className="font-display text-h2 font-black uppercase leading-[1.05] tracking-[-0.01em] text-ink md:text-display-2">
            Run your coaching from one desk.
          </h1>
          <p className="max-w-[52ch] text-[16px] text-ink-muted">
            The desk side of Lift2Fit: your roster, your clients’ programmes, your calendar and bookings, your
            payments — with AI assistance reading across every client. Your own training lives in the lifter
            profile every paid coaching plan includes. Built for coaches, performance gyms, studios and recovery
            centres.
          </p>
          <ul className="flex flex-col gap-2 text-[13px] text-ink-muted" data-testid="crm-signup-promise">
            {FREE_TIER_POINTS.map((point) => (
              <li key={point} className="flex items-start gap-2">
                <CheckIcon className="mt-[2px] shrink-0 text-accent" />
                <span className="leading-snug">{point}</span>
              </li>
            ))}
          </ul>
        </section>

        {/* right: the form */}
        <section className="flex w-full flex-col justify-center md:max-w-[440px]">
          <div className="border border-hairline bg-surface p-6 shadow-card">
            <h2 className="text-[13px] font-bold uppercase tracking-[0.1em] text-ink">
              Create your coaching business
            </h2>
            <p className="mt-1 text-[13px] text-ink-muted">
              The free product is the lifter app. Coach is £19.99/month for managing your own clients, up to
              10. Gyms is £49/month: your clients plus up to 10 internal coaches, up to 20 clients. Gyms Plus is
              £99/month: up to 20 internal coaches and up to 100 clients. Beyond that, Scale is a conversation
              at info@lift2fit.com.
            </p>

            <form className="mt-5 flex flex-col gap-4" onSubmit={submit} noValidate>
              <label className="flex flex-col gap-1.5">
                <span className="text-[11px] font-bold uppercase tracking-[0.1em] text-ink-muted">
                  Business name
                </span>
                <input
                  type="text"
                  name="businessName"
                  autoComplete="organization"
                  value={businessName}
                  onChange={(event) => setBusinessName(event.target.value)}
                  data-testid="crm-signup-name"
                  className="h-11 border border-hairline bg-page px-3 text-[15px] text-ink"
                />
              </label>

              <label className="flex flex-col gap-1.5">
                <span className="text-[11px] font-bold uppercase tracking-[0.1em] text-ink-muted">Email</span>
                <input
                  type="email"
                  name="email"
                  autoComplete="email"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  data-testid="crm-signup-email"
                  className="h-11 border border-hairline bg-page px-3 text-[15px] text-ink"
                />
              </label>

              <label className="flex flex-col gap-1.5">
                <span className="text-[11px] font-bold uppercase tracking-[0.1em] text-ink-muted">Password</span>
                <input
                  type="password"
                  name="password"
                  autoComplete="new-password"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  data-testid="crm-signup-password"
                  className="h-11 border border-hairline bg-page px-3 text-[15px] text-ink"
                />
              </label>

              {/* 0024 — the same consent control as the lifter signup, unticked. */}
              <div className="flex flex-col gap-1.5">
                <label className="flex items-start gap-2.5">
                  <input
                    type="checkbox"
                    name="acceptPolicy"
                    checked={acceptedPolicy}
                    onChange={(event) => {
                      setAcceptedPolicy(event.target.checked);
                      if (event.target.checked) setConsentError(false);
                    }}
                    data-testid="crm-signup-policy-checkbox"
                    data-policy-version={POLICY_VERSION}
                    aria-invalid={consentError}
                    aria-describedby={consentError ? 'crm-signup-consent-error' : 'crm-signup-consent-help'}
                    className="mt-[3px] h-4 w-4 shrink-0"
                  />
                  <span className="text-[13px] leading-snug text-ink-muted">
                    I have read and understand the{' '}
                    <Link
                      to="/legal/privacy"
                      onClick={(event) => event.stopPropagation()}
                      className="font-bold text-accent hover:underline"
                    >
                      Privacy Policy
                    </Link>
                    ,{' '}
                    <Link
                      to="/legal/terms"
                      onClick={(event) => event.stopPropagation()}
                      className="font-bold text-accent hover:underline"
                    >
                      Terms
                    </Link>{' '}
                    and{' '}
                    <Link
                      to="/legal/health-disclaimer"
                      onClick={(event) => event.stopPropagation()}
                      className="font-bold text-accent hover:underline"
                    >
                      Health Disclaimer
                    </Link>
                    .
                  </span>
                </label>
                <p id="crm-signup-consent-help" className="pl-[26px] text-[12px] leading-snug text-ink-muted">
                  {CONSENT_SUBLINE}
                </p>
                {consentError ? (
                  <p
                    id="crm-signup-consent-error"
                    role="alert"
                    data-testid="crm-signup-consent-error"
                    className="pl-[26px] text-[12px] font-bold text-danger"
                  >
                    Tick “I have read and understand…” before creating your workspace.
                  </p>
                ) : null}
              </div>

              <Button type="submit" size="lg" disabled={busy} className="w-full" data-testid="crm-signup-submit">
                {busy ? 'Working…' : 'Start free'}
                {!busy ? <ArrowRightIcon /> : null}
              </Button>
            </form>

            <LiveMessage message={message} tone={tone} />

            <p className="mt-2 flex items-center gap-1.5 text-[12px] text-ink-muted">
              <LockIcon className="text-[13px]" />
              Passwords are hashed with scrypt. Tokens stay on this device.
            </p>
          </div>

          <p className="mt-4 text-center text-[13px] text-ink-muted">
            Already coaching with us?{' '}
            <Link to="/crm/login" data-testid="crm-signup-login-link" className="font-bold text-accent hover:underline">
              Log in
            </Link>
          </p>
          <p className="mt-2 text-center text-[12px] text-ink-muted">
            Here for your own training?{' '}
            <Link to="/signup" data-testid="crm-signup-lifter-link" className="font-bold text-accent hover:underline">
              Create a lifter account
            </Link>{' '}
            — it lands on the tracker dashboard.
          </p>
          <p className="mt-2 text-center text-[12px] text-ink-muted">
            Service status:{' '}
            <span className={session.source === 'api' ? 'font-bold text-success' : 'text-ink-muted'}>
              {session.source === 'api' ? 'API reachable' : 'demo dataset'}
            </span>{' '}
            · <span className="l2f-tabular font-mono">{API_BASE.replace(/^https?:\/\//, '')}</span>
          </p>
        </section>
      </div>
    </div>
  );
}
